import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useTranslation } from 'react-i18next'

import { Box, Flex } from 'rebass'
import TokenLogo from 'components/TokenImage'
import { CustomLink } from '../Link'
import Row from 'components/Row'

import { formattedNum, formattedPercent } from 'utils/infos'
import { useMedia } from 'react-use'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { OVERVIEW_TOKEN_BLACKLIST } from 'constants/index'
import FormattedName from '../FormattedName'
import { TYPE } from 'theme'
import { TokenData, TokenDataState } from 'hooks/infos/TokenData'

dayjs.extend(utc)

const PageButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 2em;
  margin-bottom: 2em;
`

const Arrow = styled.div<{ faded?: boolean }>`
  color: ${({ theme }) => theme.v2.t01};
  opacity: ${(props) => (props.faded ? 0.3 : 1)};
  padding: 0 20px;
  user-select: none;
  :hover {
    cursor: pointer;
  }
`

const List = styled(Box)`
  -webkit-overflow-scrolling: touch;
`

const DashGrid = styled.div`
  display: grid;
  grid-gap: 1em;
  grid-template-columns: 50px 100px 1fr 1fr;
  grid-template-areas: '# symbol liq vol';

  > * {
    justify-content: flex-end;

    &:first-child {
      justify-content: flex-start;
      text-align: left;
      width: 30px;
    }
  }

  @media screen and (min-width: 680px) {
    display: grid;
    grid-gap: 1em;
    grid-template-columns: 50px 180px 1fr 1fr 1fr;
    grid-template-areas: '# symbol name liq vol ';

    > * {
      justify-content: center;
      width: 100%;

      &:first-child {
        justify-content: flex-start;
      }

      &:last-child {
        justify-content: end;
      }
    }
  }

  @media screen and (min-width: 1080px) {
    display: grid;
    grid-gap: 0.5em;
    grid-template-columns: 50px 0.6fr 1.5fr 1fr 1fr 1fr 1fr;
    grid-template-areas: '# symbol name liq vol price change';
  }
`

const ListWrapper = styled.div``

const ClickableText = styled(TYPE.v2Main)`
  text-align: center;
  &:hover {
    cursor: pointer;
    opacity: 0.6;
  }
  user-select: none;
  color: ${({ theme }) => theme.v2.t02};
  font-size: 12px !important;
`

const DataText = styled(TYPE.mainLg)`
  display: flex;
  align-items: center;
  text-align: center;
  color: ${({ theme }) => theme.v2.t01};

  & > * {
    font-size: 14px;
  }

  @media screen and (max-width: 600px) {
    font-size: 12px;
  }
`

const SORT_FIELD = {
  LIQ: 'totalLiquidityUSD',
  VOL: 'oneDayVolumeUSD',
  SYMBOL: 'symbol',
  NAME: 'name',
  PRICE: 'priceUSD',
  CHANGE: 'priceChangeUSD',
}

// @TODO rework into virtualized list
function TopTokenList({
  tokens,
  itemMax = 10,
}: {
  tokens: TokenDataState
  itemMax?: number
} & RouteComponentProps<any>) {
  const { t } = useTranslation()
  // page state
  const [page, setPage] = useState(1)
  const [maxPage, setMaxPage] = useState(1)

  // sorting
  const [sortDirection, setSortDirection] = useState(true)
  const [sortedColumn, setSortedColumn] = useState(SORT_FIELD.LIQ)

  const below1080 = useMedia('(max-width: 1080px)')
  const below680 = useMedia('(max-width: 680px)')
  const below600 = useMedia('(max-width: 600px)')

  useEffect(() => {
    setMaxPage(1) // edit this to do modular
    setPage(1)
  }, [tokens])

  const formattedTokens = useMemo(() => {
    return (
      tokens &&
      Object.keys(tokens)
        .filter((key) => {
          return !OVERVIEW_TOKEN_BLACKLIST.includes(key)
        })
        .map((key) => tokens[key])
    )
  }, [tokens])

  useEffect(() => {
    if (tokens && formattedTokens) {
      let extraPages = 1
      if (formattedTokens.length % itemMax === 0) {
        extraPages = 0
      }
      setMaxPage(Math.floor(formattedTokens.length / itemMax) + extraPages)
    }
  }, [tokens, formattedTokens, itemMax])

  const filteredList = useMemo(() => {
    return (
      formattedTokens &&
      formattedTokens
        .sort((a: any, b: any) => {
          if (sortedColumn === SORT_FIELD.SYMBOL || sortedColumn === SORT_FIELD.NAME) {
            return a[sortedColumn] > b[sortedColumn] ? (sortDirection ? -1 : 1) * 1 : (sortDirection ? -1 : 1) * -1
          }
          return parseFloat(a[sortedColumn]) > parseFloat(b[sortedColumn])
            ? (sortDirection ? -1 : 1) * 1
            : (sortDirection ? -1 : 1) * -1
        })
        .slice(itemMax * (page - 1), page * itemMax)
    )
  }, [formattedTokens, itemMax, page, sortDirection, sortedColumn])

  const ListItem = ({ item, index }: { item: TokenData; index: number }) => {
    return (
      <DashGrid style={{ height: '48px' }}>
        <DataText>{index}</DataText>
        <DataText>
          <Row justify="flex-start">
            <TokenLogo address={item.id} />
            <CustomLink style={{ marginLeft: '16px', whiteSpace: 'nowrap' }} to={'/analytics/tokens/' + item.id}>
              <FormattedName text={item.symbol} maxCharacters={below600 ? 8 : 12} adjustSize={true} link={true} />
            </CustomLink>
          </Row>
        </DataText>
        {!below680 && (
          <DataText color="text">
            <FormattedName text={item.name} maxCharacters={16} />
          </DataText>
        )}
        <DataText textAlign="center">{formattedNum(item.totalLiquidityUSD || 0, true)}</DataText>
        <DataText textAlign="center">{formattedNum(item.oneDayVolumeUSD || 0, true)}</DataText>
        {!below1080 && (
          <DataText color="text" textAlign="center">
            {formattedNum(item.priceUSD || 0, true)}
          </DataText>
        )}
        {!below1080 && <DataText textAlign="right">{formattedPercent(item.priceChangeUSD || 0)}</DataText>}
      </DashGrid>
    )
  }

  return (
    <ListWrapper>
      <DashGrid style={{ height: 'fit-content', paddingBottom: '16px' }}>
        <Flex>
          <TYPE.v2Main color="t02" fontSize={12}>
            #
          </TYPE.v2Main>
        </Flex>
        <Flex alignItems="center" justifyContent="flex-start">
          <ClickableText
            onClick={(e) => {
              setSortedColumn(SORT_FIELD.SYMBOL)
              setSortDirection(sortedColumn !== SORT_FIELD.SYMBOL ? true : !sortDirection)
            }}
            textAlign="left"
          >
            {t('analytics.symbol')} {sortedColumn === SORT_FIELD.SYMBOL ? (!sortDirection ? '↑' : '↓') : ''}
          </ClickableText>
        </Flex>
        {!below680 && (
          <Flex alignItems="center" justifyContent="center">
            <ClickableText
              onClick={(e) => {
                setSortedColumn(SORT_FIELD.NAME)
                setSortDirection(sortedColumn !== SORT_FIELD.NAME ? true : !sortDirection)
              }}
              textAlign="center"
            >
              {t('analytics.name')} {sortedColumn === SORT_FIELD.NAME ? (!sortDirection ? '↑' : '↓') : ''}
            </ClickableText>
          </Flex>
        )}

        <Flex alignItems="center" justifyContent="center">
          <ClickableText
            onClick={(e) => {
              setSortedColumn(SORT_FIELD.LIQ)
              setSortDirection(sortedColumn !== SORT_FIELD.LIQ ? true : !sortDirection)
            }}
            textAlign="center"
          >
            {t('analytics.liquidity')} {sortedColumn === SORT_FIELD.LIQ ? (!sortDirection ? '↑' : '↓') : ''}
          </ClickableText>
        </Flex>
        <Flex alignItems="center" justifyContent="center">
          <ClickableText
            onClick={(e) => {
              setSortedColumn(SORT_FIELD.VOL)
              setSortDirection(sortedColumn !== SORT_FIELD.VOL ? true : !sortDirection)
            }}
            textAlign="center"
          >
            {t('analytics.volume_24hrs')}
            {sortedColumn === SORT_FIELD.VOL ? (!sortDirection ? '↑' : '↓') : ''}
          </ClickableText>
        </Flex>
        {!below1080 && (
          <Flex alignItems="center" justifyContent="center">
            <ClickableText
              onClick={(e) => {
                setSortedColumn(SORT_FIELD.PRICE)
                setSortDirection(sortedColumn !== SORT_FIELD.PRICE ? true : !sortDirection)
              }}
              textAlign="center"
            >
              Price {sortedColumn === SORT_FIELD.PRICE ? (!sortDirection ? '↑' : '↓') : ''}
            </ClickableText>
          </Flex>
        )}
        {!below1080 && (
          <Flex alignItems="center" justifyContent="flex-end">
            <ClickableText
              onClick={(e) => {
                setSortedColumn(SORT_FIELD.CHANGE)
                setSortDirection(sortedColumn !== SORT_FIELD.CHANGE ? true : !sortDirection)
              }}
              textAlign="right"
            >
              {t('analytics.price_change_24hrs')}
              {sortedColumn === SORT_FIELD.CHANGE ? (!sortDirection ? '↑' : '↓') : ''}
            </ClickableText>
          </Flex>
        )}
      </DashGrid>
      <List p={0}>
        {filteredList &&
          filteredList.map((item, index) => {
            return (
              <div key={index}>
                <ListItem key={index} index={(page - 1) * itemMax + index + 1} item={item} />
              </div>
            )
          })}
      </List>
      <PageButtons>
        <div onClick={() => setPage(page === 1 ? page : page - 1)}>
          <Arrow faded={page === 1 ? true : false}>
            <i className="iconfont icon-page-prev" />
          </Arrow>
        </div>
        <TYPE.v2Main>{t('analytics.page_of', { current: page, total: maxPage })}</TYPE.v2Main>
        <div onClick={() => setPage(page === maxPage ? page : page + 1)}>
          <Arrow faded={page === maxPage ? true : false}>
            <i className="iconfont icon-page-next" />
          </Arrow>
        </div>
      </PageButtons>
    </ListWrapper>
  )
}

export default withRouter(TopTokenList)
